<template>
  <div class="main">
    <PopupAlert v-show="$Session.get($userSessionName).isTrial" />

    <lila-terms
      :show-accept-terms="false"
      :modal-active="activeModal"
    />

    <lila-tutorial ref="tutorial" />

    <OnboardingTutorial
      ref="onboardingTutorial"
      :has-tickets="hasTicketsForTutorial"
      @complete="onTutorialComplete"
    />

    <div
      class="sidebar-overlay"
      :class="{ 'is-open': burgerActive }"
      @click="closeBurger"
    />

    <div class="app-shell">
      <!-- Navegação -->
      <aside
        class="sidebar"
        :class="{ 'is-open': burgerActive, 'is-collapsed': collapsed }"
        role="navigation"
        aria-label="Menu principal"
      >
        <div class="sidebar-header">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            class="logo-lila"
          >
        </div>

        <div
          v-if="institutionName"
          class="sidebar-institution"
          :title="collapsed ? institutionName : null"
        >
          <span class="institution-avatar">
            <i
              class="fas fa-hospital"
              aria-hidden="true"
            />
          </span>
          <span class="institution-text">
            <span class="institution-label">Instituição</span>
            <span class="institution-name">{{ institutionName }}</span>
          </span>
        </div>

        <nav
          ref="sidebarNav"
          class="sidebar-nav"
          :class="{ 'sidebar-nav--single': groupedMenu.length <= 1 }"
          :style="{ '--open-submenus-height': `${openSubmenusHeight}px` }"
        >
          <template
            v-for="group in groupedMenu"
            :key="group.label"
          >
            <span class="sidebar-group-label">{{ group.label }}</span>

            <router-link
              v-for="route in group.items"
              :key="route.name"
              class="sidebar-item"
              :to="{ name: route.name }"
              :data-tutorial="getTutorialAttribute(route.name)"
              @mouseenter="setItemTitle($event, route.title)"
              @click="closeBurger"
            >
              <i
                class="sidebar-icon fas"
                :class="route.icon"
                aria-hidden="true"
              />
              <span class="sidebar-label">{{ route.title }}</span>
            </router-link>

            <template
              v-for="submenu in group.submenus"
              :key="submenu.key"
            >
              <button
                type="button"
                class="sidebar-item sidebar-submenu-toggle"
                :class="{ 'is-active': isSubmenuActive(submenu) }"
                :aria-expanded="isSubmenuOpen(submenu)"
                @mouseenter="setItemTitle($event, submenu.label)"
                @click="toggleSubmenu(submenu.key)"
              >
                <i
                  class="sidebar-icon fas"
                  :class="submenu.icon"
                  aria-hidden="true"
                />
                <span class="sidebar-label">{{ submenu.label }}</span>
                <i
                  class="sidebar-submenu-caret fas fa-chevron-down"
                  :class="{ 'is-open': isSubmenuOpen(submenu) }"
                  aria-hidden="true"
                />
              </button>

              <div
                v-show="isSubmenuOpen(submenu)"
                class="sidebar-submenu"
              >
                <router-link
                  v-for="route in submenu.items"
                  :key="route.name"
                  class="sidebar-item sidebar-subitem"
                  :to="{ name: route.name }"
                  @mouseenter="setItemTitle($event, route.title)"
                  @click="closeBurger"
                >
                  <i
                    class="sidebar-icon fas"
                    :class="route.icon"
                    aria-hidden="true"
                  />
                  <span class="sidebar-label">{{ route.title }}</span>
                </router-link>
              </div>
            </template>
          </template>
        </nav>

        <div class="sidebar-footer">
          <button
            class="sidebar-collapse-btn"
            :aria-label="collapsed ? 'Expandir menu' : 'Recolher menu'"
            :title="collapsed ? 'Expandir menu' : 'Recolher menu'"
            @click="toggleCollapse"
          >
            <svg
              class="sidebar-collapse-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="2"
              />
              <line
                x1="9"
                y1="4"
                x2="9"
                y2="20"
              />
            </svg>
            <span class="sidebar-collapse-label">Recolher menu</span>
          </button>
        </div>
      </aside>

      <div class="app-main">
        <!-- Faixa superior: ações de conta + alertas (canto direito) -->
        <header class="app-topbar">
          <button
            class="topbar-burger"
            :aria-expanded="burgerActive ? 'true' : 'false'"
            aria-label="Abrir menu"
            @click="toggleBurger"
          >
            <i class="fas fa-bars" />
          </button>

          <img
            v-if="logoUrl"
            :src="logoUrl"
            class="topbar-logo logo-lila"
          >

          <div class="topbar-actions">
            <lila-action-notifications />

            <div
              v-if="accountMenu"
              ref="accountWrapper"
              class="topbar-account"
            >
              <button
                class="account-trigger"
                :aria-label="`Minha conta, ${userName}`"
                :aria-expanded="accountOpen ? 'true' : 'false'"
                aria-haspopup="true"
                @click.stop="toggleAccount"
              >
                <img
                  :src="userAvatar"
                  class="account-avatar circular-mask"
                  alt=""
                >
                <i
                  class="account-caret fas fa-chevron-down"
                  :class="{ 'account-caret--open': accountOpen }"
                  aria-hidden="true"
                />
              </button>

              <transition name="account-pop">
                <div
                  v-show="accountOpen"
                  class="account-popover"
                  role="menu"
                >
                  <div class="account-popover-header">
                    <img
                      :src="userAvatar"
                      class="account-popover-avatar circular-mask"
                      alt=""
                    >
                    <span class="account-popover-identity">
                      <span class="account-popover-name">{{ userName }}</span>
                      <span class="account-popover-sub">{{
                        institutionName
                      }}</span>
                    </span>
                  </div>

                  <div class="account-popover-list">
                    <template
                      v-for="submenuRoute in filterItemMenu(
                        accountMenu.children
                      )"
                      :key="submenuRoute.name"
                    >
                      <a
                        v-if="submenuRoute.name == 'termsOfUse'"
                        href="#"
                        class="account-popover-item"
                        role="menuitem"
                        @click.prevent="openTerms"
                      >
                        <i
                          class="account-popover-icon fas"
                          :class="accountIcon(submenuRoute.name)"
                          aria-hidden="true"
                        />
                        <span>{{ submenuRoute.title }}</span>
                      </a>

                      <a
                        v-else-if="submenuRoute.name == 'tutorial'"
                        href="#"
                        class="account-popover-item"
                        role="menuitem"
                        @click.prevent="openTutorial"
                      >
                        <i
                          class="account-popover-icon fas"
                          :class="accountIcon(submenuRoute.name)"
                          aria-hidden="true"
                        />
                        <span>{{ submenuRoute.title }}</span>
                      </a>

                      <a
                        v-else-if="verifyItemAction(submenuRoute) == 'logout'"
                        href="#"
                        class="account-popover-item is-logout"
                        role="menuitem"
                        @click.prevent="logout"
                      >
                        <i
                          class="account-popover-icon fas"
                          :class="accountIcon(submenuRoute.name)"
                          aria-hidden="true"
                        />
                        <span>{{ submenuRoute.title }}</span>
                      </a>

                      <router-link
                        v-else-if="verifyAccess(submenuRoute)"
                        class="account-popover-item"
                        role="menuitem"
                        :to="{ name: submenuRoute.name }"
                        @click="closeAccount"
                      >
                        <i
                          class="account-popover-icon fas"
                          :class="accountIcon(submenuRoute.name)"
                          aria-hidden="true"
                        />
                        <span>{{ submenuRoute.title }}</span>
                      </router-link>
                    </template>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </header>

        <section
          class="content main-section app-content"
          :class="mainSectionClass"
        >
          <div
            v-show="loadingSignalR"
            class="loading signalr"
          />

          <div v-show="!loadingContent">
            <slot name="mainContent" />

            <p class="app-version">
              {{ $app_version }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Terms from "@components/shared/lila/Terms.vue";
import Tutorial from "@components/shared/lila/Tutorial.vue";
import PopupAlert from "@/components/PatientTicketList/LilaPopUpAlert.vue";
import ActionNotifications from "@components/Actions/ActionNotifications.vue";
import { OnboardingTutorial } from "@components/shared/lila/OnboardingTutorial";

export default {
  components: {
    "lila-terms": Terms,
    "lila-tutorial": Tutorial,
    "lila-action-notifications": ActionNotifications,
    PopupAlert,
    OnboardingTutorial
  },
  props: {
    loadingContent: {
      required: false,
      type: Boolean,
      default: false
    },
    loadingSignalR: {
      required: false,
      type: Boolean,
      default: false
    },
    alertMessage: {
      required: false,
      type: Object,
      default: () => {
        return {
          message: "",
          type: "",
          show: false
        };
      }
    }
  },
  data() {
    return {
      pageName: "",
      institutionName: "",
      userName: "",
      userGender: "",
      activeModal: false,
      burgerActive: false,
      accountOpen: false,
      collapsed: localStorage.getItem("sidebarCollapsed") === "true",
      logoUrl: null,
      hasTicketsForTutorial: true,
      // Ícone (Font Awesome) de cada item de navegação.
      menuIcons: {
        patientTickets: "fa-inbox",
        myPatients: "fa-users",
        journey: "fa-route",
        ambulatory: "fa-stethoscope",
        calendar: "fa-calendar-alt",
        acoes: "fa-tasks",
        communicated: "fa-bullhorn",
        reports: "fa-chart-line",
        palliativeNetwork: "fa-project-diagram",
        susProduction: "fa-file-medical",
        professionals: "fa-user-md",
        units: "fa-hospital",
        licenses: "fa-award",
        bi: "fa-chart-bar",
        automaticMessages: "fa-comment-dots",
        historyRecordTypes: "fa-list-alt"
      },
      // Grupos do menu (rótulo + ordem dos itens).
      menuGroups: [
        {
          label: "Atendimento",
          items: [
            "patientTickets",
            "myPatients",
            "ambulatory",
            "journey",
            "calendar",
            "acoes",
            "communicated"
          ]
        },
        {
          label: "Gestão",
          management: true,
          items: [
            "reports",
            "palliativeNetwork",
            "susProduction",
            "professionals",
            "units",
            "licenses",
            "bi"
          ],
          submenus: [
            {
              key: "settings",
              label: "Configurações",
              icon: "fa-cog",
              items: ["automaticMessages"]
            }
          ]
        }
      ],
      openSubmenus: [],
      openSubmenusHeight: 0
    };
  },
  computed: {
    itensMenu() {
      return this.$router.options.routes.filter(
        (item) => typeof item.title !== "undefined" && this.verifyAccess(item)
      );
    },
    groupedMenu() {
      return this.menuGroups
        .filter(
          (group) =>
            !group.management || this.$Utilities.hasManagementAccess(this)
        )
        .map((group) => ({
          label: group.label,
          items: group.items
            .map((name) => this.findMenuRoute(name))
            .filter(Boolean),
          submenus: (group.submenus || [])
            .map((submenu) => ({
              ...submenu,
              items: submenu.items
                .map((name) => this.findMenuRoute(name))
                .filter(Boolean)
            }))
            .filter((submenu) => submenu.items.length > 0)
        }))
        .filter((group) => group.items.length > 0 || group.submenus.length > 0);
    },
    accountMenu() {
      return this.itensMenu.find(
        (route) => this.verifyItemAction(route) == "submenu"
      );
    },
    userAvatar() {
      const photo = this.$Session.get(this.$userSessionName).professional.Photo;
      if (photo) return photo;

      if (!this.userGender)
        return require("@/assets/images/default_avatar_neutral.png");

      return this.userGender.toLowerCase() === "masculino"
        ? require("@/assets/images/default_avatar_male.png")
        : require("@/assets/images/default_avatar_female.png");
    },
    mainSectionClass() {
      let sectionClass = this.$hasDoctorBg.includes(this.pageName)
        ? "doctor-bg"
        : "section";

      if (this.loadingContent) sectionClass += " loading large ";

      return sectionClass;
    }
  },
  watch: {
    groupedMenu() {
      this.openActiveSubmenus();
    },
    $route() {
      this.openActiveSubmenus();
    }
  },
  created() {
    this.userName = this.$Session.get(this.$userSessionName).professional.Name;
    this.institutionName = this.$Session.get(
      this.$userSessionName
    ).professional.Institution.Name;
    this.userGender = this.$Session.get(
      this.$userSessionName
    ).professional.Gender;

    this.pageName = this.$route.name;

    let logoUrl = this.$Session.get(this.$userSessionName).logoUrl;

    if (logoUrl) {
      this.logoUrl = logoUrl;
    } else {
      this.logoUrl = require("@/assets/images/logo-lila.png");
    }

    this.checkAndStartTutorial();
  },
  mounted() {
    document.addEventListener("click", this.handleAccountClickOutside);
    document.addEventListener("keydown", this.handleAccountEscape);
    window.addEventListener("resize", this.updateNavOffset);
    this.openActiveSubmenus();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleAccountClickOutside);
    document.removeEventListener("keydown", this.handleAccountEscape);
    window.removeEventListener("resize", this.updateNavOffset);
  },
  methods: {
    setItemTitle(event, label) {
      const item = event.currentTarget;
      const text = item.querySelector(".sidebar-label");
      const isTruncated = text && text.scrollWidth > text.clientWidth;

      item.title = this.collapsed || isTruncated ? label : "";
    },
    updateNavOffset() {
      const nav = this.$refs.sidebarNav;
      if (!nav) return;

      this.openSubmenusHeight = [...nav.querySelectorAll(".sidebar-submenu")]
        .reduce((total, submenu) => total + submenu.offsetHeight, 0);
    },
    openActiveSubmenus() {
      const activeKeys = this.groupedMenu
        .flatMap((group) => group.submenus)
        .filter((submenu) => this.isSubmenuActive(submenu))
        .map((submenu) => submenu.key);

      this.openSubmenus = [...new Set([...this.openSubmenus, ...activeKeys])];
      this.$nextTick(this.updateNavOffset);
    },
    isSubmenuActive(submenu) {
      return submenu.items.some((route) => route.name === this.$route.name);
    },
    isSubmenuOpen(submenu) {
      return this.openSubmenus.includes(submenu.key);
    },
    toggleSubmenu(key) {
      this.openSubmenus = this.openSubmenus.includes(key)
        ? this.openSubmenus.filter((item) => item !== key)
        : [...this.openSubmenus, key];
      this.$nextTick(this.updateNavOffset);
    },
    findMenuRoute(name) {
      const route = this.itensMenu.find((item) => item.name === name);
      if (!route || this.verifyItemAction(route) != "router") return null;

      return { ...route, icon: this.menuIcons[route.name] };
    },
    verifyAccess(route) {
      if (route.name == "acoes")
        return this.$Utilities.hasAccessToFeature(this, 7);

      if (route.name == "calendar")
        return this.$Utilities.hasAccessToFeature(this, 10);

      if (route.name == "journey")
        return this.$Utilities.hasAccessToFeature(this, 14);

      if (route.name == "automaticMessages")
        return (
          this.$Utilities.hasManagementAccess(this) &&
          this.$Utilities.hasAccessToFeature(this, 15)
        );

      if (route.name == "bi")
        return this.$Utilities.hasAccessToFeature(this, 5);

      // POC Cuidados Paliativos: o ambulatório é do hospital, não da equipe matricial (role 2).
      if (route.name == "ambulatory")
        return (
          this.$Utilities.hasPalliativeCareAccess(this) &&
          this.$Utilities.getProfessionalRole(this) !== 2
        );

      // POC Cuidados Paliativos: "Rede de CP" só para a equipe matricial (role 2).
      if (route.name == "palliativeNetwork")
        return this.$Utilities.isPalliativeMatrixTeam(this);

      if (route.name == "susProduction")
        return this.$Utilities.hasPalliativeCareAccess(this);

      return true;
    },
    verifyItemAction(itemMenu) {
      return typeof itemMenu.action !== "undefined"
        ? itemMenu.action
        : "router";
    },
    filterItemMenu(itemMenu) {
      return itemMenu.filter((item) => typeof item.title != "undefined");
    },
    accountIcon(name) {
      const map = {
        registrationData: "fa-user",
        changePassword: "fa-lock",
        tutorial: "fa-question-circle",
        termsOfUse: "fa-file-alt",
        whoWeAre: "fa-info-circle",
        logout: "fa-sign-out-alt"
      };
      return map[name] || "fa-circle";
    },
    openTerms() {
      this.closeAccount();
      this.activeModal = !this.activeModal;
    },
    openTutorial() {
      this.closeAccount();
      this.$refs.tutorial.openModal();
    },
    toggleAccount() {
      this.accountOpen = !this.accountOpen;
    },
    closeAccount() {
      this.accountOpen = false;
    },
    handleAccountClickOutside(event) {
      if (
        this.accountOpen &&
        this.$refs.accountWrapper &&
        !this.$refs.accountWrapper.contains(event.target)
      ) {
        this.accountOpen = false;
      }
    },
    handleAccountEscape(event) {
      if (event.key === "Escape" && this.accountOpen) {
        this.accountOpen = false;
      }
    },
    checkAndStartTutorial() {
      const session = this.$Session.get(this.$userSessionName);
      if (session && session.showTutorial) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.startOnboardingTutorial();
          }, 500);
        });
      }
    },
    startOnboardingTutorial(hasTickets = true) {
      this.hasTicketsForTutorial = hasTickets;
      if (this.$refs.onboardingTutorial) {
        this.$refs.onboardingTutorial.start(hasTickets);
      }
    },
    onTutorialComplete() {
      const session = this.$Session.get(this.$userSessionName);
      if (session) {
        session.showTutorial = false;
        this.$Session.set(this.$userSessionName, session);
      }
    },
    logout() {
      this.$Session.remove(this.$userSessionName);

      this.$router.push({ name: "login" });
    },
    toggleBurger() {
      this.burgerActive = !this.burgerActive;
    },
    closeBurger() {
      this.burgerActive = false;
    },
    toggleCollapse() {
      this.$nextTick(this.updateNavOffset);
      this.collapsed = !this.collapsed;
      localStorage.setItem("sidebarCollapsed", this.collapsed);
    },
    getImagePath(fileName) {
      return require(`@/assets/images/${fileName}`);
    },
    getTutorialAttribute(routeName) {
      const tutorialMap = {
        calendar: "menu-agenda",
        myPatients: "menu-pacientes",
        reports: "menu-relatorios",
        professionals: "menu-profissionais"
      };
      return tutorialMap[routeName] || null;
    }
  }
};
</script>

<style>
@import "~@assets/css/main.css";
@import "~@assets/css/components/sidebar.css";
</style>
